import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Star, Award, Globe2, ChevronRight } from "lucide-react";
import { SmartImage } from "@/components/SmartImage";
import { TourCard } from "@/components/india/TourCard";
import { AWARDS } from "@/data/indiaContent";
import { norm } from "@/lib/search";

export const SectionTitle = ({ children, dark = false }) => (
  <div className="text-center mb-9">
    <motion.h2
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`font-serif-display text-2xl md:text-[32px] brush-underline ${dark ? "text-white" : "text-[var(--navy)]"}`}
    >
      {children}
    </motion.h2>
  </div>
);

const TabRow = ({ tabs, active, onChange }) => (
  <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
    {tabs.map((t) => (
      <button
        key={t}
        type="button"
        onClick={() => onChange(t)}
        data-testid={`tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
        className={`px-5 py-2.5 rounded-md text-[13.5px] font-semibold border transition-all ${
          active === t
            ? "bg-[var(--navy)] text-white border-[var(--navy)] shadow-md"
            : "bg-white text-[#0b2545]/70 border-gray-200 hover:border-[var(--navy)]"
        }`}
      >
        {t}
      </button>
    ))}
  </div>
);

const Scroller = ({ children, testid }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  return (
    <div className="relative">
      <div className="flex justify-end gap-2 mb-3">
        <button type="button" onClick={() => scroll(-1)} className="w-9 h-9 rounded-full border border-gray-300 text-[var(--navy)] flex items-center justify-center hover:bg-[var(--navy)] hover:text-white transition" aria-label="prev">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => scroll(1)} className="w-9 h-9 rounded-full border border-gray-300 text-[var(--navy)] flex items-center justify-center hover:bg-[var(--navy)] hover:text-white transition" aria-label="next">
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <div ref={ref} data-testid={testid} className="flex gap-5 overflow-x-auto no-scrollbar pb-2 snap-x">
        {children}
      </div>
    </div>
  );
};

const PortraitCard = ({ item, index, onSelect }) => (
  <motion.button
    type="button"
    initial={{ opacity: 0, scale: 0.96 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.45, delay: (index % 6) * 0.05 }}
    onClick={() => onSelect?.(item.name)}
    data-testid={`place-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="group relative w-[200px] h-[280px] shrink-0 rounded-xl overflow-hidden snap-start text-left"
  >
    <SmartImage src={item.image} alt={item.name} fallbackIndex={index} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
    <div className="absolute bottom-0 inset-x-0 p-4 text-white">
      <div className="font-semibold text-[16px] leading-tight">{item.name}</div>
      <div className="text-[12px] text-white/80 mt-0.5">{item.tours} {item.tours === 1 ? "tour" : "tours"}</div>
    </div>
  </motion.button>
);

const LandscapeCard = ({ item, index, onSelect }) => (
  <motion.button
    type="button"
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.45, delay: (index % 4) * 0.05 }}
    onClick={() => onSelect?.(item.name)}
    data-testid={`zone-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="group text-left"
  >
    <div className="rounded-xl overflow-hidden h-40 mb-2">
      <SmartImage src={item.image} alt={item.name} fallbackIndex={index} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
    </div>
    <div className="font-semibold text-[15px] text-[var(--navy)] group-hover:text-[var(--yellow-2)] transition leading-tight">{item.name}</div>
    <div className="text-[12.5px] text-gray-500">{item.tours} {item.tours === 1 ? "tour" : "tours"}</div>
  </motion.button>
);

// Primary: By City/State  OR  By Country/City
export const PrimarySection = ({ config, onSelect }) => {
  const [tab, setTab] = useState(config.tabs[0].label);
  const data = config.tabs.find((t) => t.label === tab)?.data ?? [];
  return (
    <section className="py-14 bg-[var(--soft)]" data-testid="section-primary">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <SectionTitle>{config.title}</SectionTitle>
        <TabRow tabs={config.tabs.map((t) => t.label)} active={tab} onChange={setTab} />
        <Scroller testid="primary-scroller">
          {data.map((item, i) => <PortraitCard key={item.name} item={item} index={i} onSelect={onSelect} />)}
        </Scroller>
      </div>
    </section>
  );
};

// By Zone / By Regions
export const RegionSection = ({ config, onSelect }) => {
  const tabs = Object.keys(config.data);
  const [tab, setTab] = useState(tabs[0]);
  return (
    <section className="py-14 bg-white" data-testid="section-region">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <SectionTitle>{config.title}</SectionTitle>
        <TabRow tabs={tabs} active={tab} onChange={setTab} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {config.data[tab].map((item, i) => <LandscapeCard key={item.name} item={item} index={i} onSelect={onSelect} />)}
        </div>
      </div>
    </section>
  );
};

export const SeasonSection = ({ config, onSelect }) => {
  const seasons = Object.keys(config.data);
  const [tab, setTab] = useState(seasons[0]);
  return (
    <section className="py-14 bg-[var(--soft)]" data-testid="section-season">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <SectionTitle>{config.title}</SectionTitle>
        <TabRow tabs={seasons} active={tab} onChange={setTab} />
        <Scroller testid="season-scroller">
          {config.data[tab].map((item, i) => <PortraitCard key={item.name} item={item} index={i} onSelect={onSelect} />)}
        </Scroller>
      </div>
    </section>
  );
};

export const InterestSection = ({ config, tours, onShowAll }) => {
  const [tab, setTab] = useState(config.tabs[0]);
  const matched = tours.filter((t) => norm(t.interest) === norm(tab) || norm(t.speciality).startsWith(norm(tab))).slice(0, 2);
  return (
    <section className="py-14 bg-white" data-testid="section-interest">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <SectionTitle>{config.title}</SectionTitle>
        <TabRow tabs={config.tabs} active={tab} onChange={setTab} />
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {matched.map((t, i) => <TourCard key={t.slug} tour={t} view="grid" index={i} />)}
          <div className="bg-gradient-to-br from-[#eef3fb] to-white border border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
            <div className="text-[17px] font-semibold text-[var(--navy)]">Journey doesn't stop at this point</div>
            <Globe2 className="w-16 h-16 text-[var(--yellow-2)] my-4" strokeWidth={1.2} />
            <p className="text-[13px] text-gray-500 mb-4">start exploring a diverse range of additional tours.</p>
            <button type="button" onClick={() => onShowAll?.(tab)} data-testid="interest-show-all" className="bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold text-[13px] px-5 py-2.5 rounded-md transition">
              Show all tours
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export const DurationSection = ({ config, onSelect }) => (
  <section className="py-14 bg-[var(--soft)]" data-testid="section-duration">
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
      <SectionTitle>{config.title}</SectionTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {config.cards.map((item, i) => (
          <motion.button
            key={item.label}
            type="button"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: i * 0.06 }}
            onClick={() => onSelect?.(item.label)}
            data-testid={`duration-card-${i}`}
            className="group relative h-48 rounded-xl overflow-hidden text-left"
          >
            <SmartImage src={item.image} alt={item.label} fallbackIndex={i} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b2545]/90 via-[#0b2545]/20 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-4 text-white">
              <div className="font-serif-display text-xl">{item.label}</div>
              <div className="text-[12.5px] text-white/85 inline-flex items-center gap-1">{item.tours} {item.tours === 1 ? "tour" : "tours"} <ChevronRight className="w-3.5 h-3.5" /></div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  </section>
);

export const PopularBlogs = ({ config }) => (
  <section className="py-14 bg-white" data-testid="section-blogs">
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
      <SectionTitle>{config.title}</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
        {config.data.map((b, i) => (
          <motion.a
            key={b.title}
            href="#"
            onClick={(e) => e.preventDefault()}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: i * 0.05 }}
            className="group"
          >
            <div className="rounded-xl overflow-hidden h-36 mb-2.5">
              <SmartImage src={b.image} alt={b.title} fallbackIndex={i} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            </div>
            <div className="text-[13.5px] font-semibold text-[var(--navy)] group-hover:text-[var(--yellow-2)] leading-snug transition">{b.title}</div>
          </motion.a>
        ))}
      </div>
    </div>
  </section>
);

export const Awards = () => (
  <section className="py-16 bg-[var(--soft)]" data-testid="section-awards">
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 text-center">
      <h2 className="font-serif-display text-2xl md:text-[34px] text-[var(--navy)]">Award-Winning Digital Trailblazers with a Global Footprint</h2>
      <p className="text-gray-500 mt-2 mb-10">We are proud to be recognized for our excellence in the travel industry.</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
        {AWARDS.map((a, i) => (
          <motion.div
            key={a.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="flex flex-col items-center"
          >
            <div className="w-20 h-24 rounded-lg bg-gradient-to-b from-[#fcd34d]/20 to-white border border-[#f59e0b]/30 flex items-center justify-center mb-3">
              <Award className="w-9 h-9 text-[var(--yellow-2)]" strokeWidth={1.4} />
            </div>
            <div className="font-semibold text-[14px] text-[var(--navy)]">{a.title}</div>
            <div className="text-[12px] text-gray-500 mt-1 leading-snug">{a.sub}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const TourReviews = ({ config }) => (
  <section className="py-16 bg-[var(--navy)] relative overflow-hidden" data-testid="section-reviews">
    <div className="absolute -left-10 top-10 text-[220px] font-serif-display text-white/5 leading-none select-none">“</div>
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 relative">
      <div className="text-center mb-10">
        <h2 className="font-serif-display text-2xl md:text-[32px] text-white">Tour Reviews</h2>
        <p className="text-white/70 mt-2">What are you waiting for? Chalo Bag Bharo Nikal Pado!</p>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {config.data.slice(0, 3).map((r, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="bg-white rounded-xl p-5 flex flex-col"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-0.5 text-[var(--yellow-2)] font-bold text-[13px]"><Star className="w-3.5 h-3.5 fill-[var(--yellow-2)]" /> {r.rating}</span>
              <span className="text-[10px] font-semibold text-[var(--red)] border border-[#e11d2e]/30 rounded px-1.5 py-0.5 uppercase">{r.tag}</span>
            </div>
            <div className="font-semibold text-[var(--navy)] mb-1.5">{r.title}</div>
            <div className="flex gap-3">
              <div className="flex-1">
                <p className="text-[13px] text-[#0b2545]/70 leading-snug">"{r.text}"</p>
                <button type="button" className="text-[13px] font-semibold text-[var(--navy)] underline mt-1">Read more</button>
              </div>
              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
                <SmartImage src={r.image} alt={r.author} fallbackIndex={i} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
              <div>
                <div className="text-[13px] font-semibold text-[var(--navy)]">{r.author}</div>
                <div className="text-[11px] text-gray-400">{r.date}</div>
              </div>
              <div className="text-[12px] text-[#0b2545]/70 underline text-right max-w-[120px]">{r.managers}</div>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="text-center mt-10">
        <button type="button" className="bg-[var(--yellow)] hover:bg-white text-[var(--navy)] font-semibold px-8 py-3 rounded-md transition" data-testid="read-reviews-btn">
          Read 15K+ Reviews
        </button>
      </div>
    </div>
  </section>
);

// "View World/India Tour Packages" departure-city chips box
export const ToursFromBox = ({ items, scope, onPick }) => (
  <div className="my-6 border border-[#0b2545]/20 rounded-xl bg-[#f0f6ff] p-5" data-testid="tours-from-box">
    <div className="font-semibold text-[var(--navy)] mb-3">View {scope === "world" ? "World" : "India"} Tour Packages</div>
    <div className="flex flex-wrap gap-2.5">
      {items.map((t) => (
        <button key={t.city} type="button" onClick={() => onPick?.(t.city)} data-testid={`tours-from-${t.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-full border border-gray-300 bg-white px-3.5 py-1.5 text-[12.5px] text-[#0b2545]/80 hover:border-[var(--navy)] hover:text-[var(--navy)] transition">
          {t.count} {t.count === 1 ? "tour" : "tours"} from {t.city}
        </button>
      ))}
      <button type="button" onClick={() => onPick?.("Joining / Leaving")} data-testid="tours-from-joining" className="rounded-full border border-[var(--yellow-2)] bg-[#fcd34d]/10 px-3.5 py-1.5 text-[12.5px] font-semibold text-[var(--navy)]">
        Joining / Leaving
      </button>
    </div>
    <p className="text-[12.5px] text-[var(--red-2)] font-medium mt-3">
      Can't find tours from your city? Check our Joining &amp; Leaving option. Book your own flights and join directly at the first destination of the tour.
    </p>
  </div>
);
